// Tracks the user's Amazon catalogue sync status across the dashboard so
// banners, buttons and pages can react consistently.

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import AuthContext from './AuthContext';

const POLL_INTERVAL_MS = 30_000;
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const DEFAULT_STATE = {
    status: 'unknown',      // unknown | not_connected | pending | syncing | complete | failed
    startedAt: null,
    completedAt: null,
    error: null,
    staleHours: 12,
    loading: false,
    lastFetchedAt: null,
};

const AmazonSyncContext = createContext({
    ...DEFAULT_STATE,
    refresh: () => {},
    isSyncing: false,
    isBridgeActive: false,
});

export const AmazonSyncProvider = ({ children }) => {
    const authCtx = useContext(AuthContext);
    const [state, setState] = useState(DEFAULT_STATE);
    const pollTimerRef = useRef(null);

    const fetchStatus = useCallback(async () => {
        if (!authCtx.token) {
            setState(s => ({ ...s, loading: false, status: 'unknown' }));
            return;
        }
        try {
            const res = await fetch(`${API_URL}/auth/amazon/sync-status`, {
                headers: { Authorization: `Bearer ${authCtx.token}` },
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setState({
                status: data.status || 'unknown',
                startedAt: data.started_at,
                completedAt: data.completed_at,
                error: data.error,
                staleHours: data.stale_hours ?? 12,
                loading: false,
                lastFetchedAt: Date.now(),
            });
        } catch (e) {
            setState(s => ({ ...s, loading: false, lastFetchedAt: Date.now() }));
        }
    }, [authCtx.token]);

    useEffect(() => { fetchStatus(); }, [fetchStatus]);

    useEffect(() => {
        if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        if (state.status === 'syncing') {
            pollTimerRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
        }
        return () => {
            if (pollTimerRef.current) {
                clearInterval(pollTimerRef.current);
                pollTimerRef.current = null;
            }
        };
    }, [state.status, fetchStatus]);

    useEffect(() => {
        const onFocus = () => {
            if (state.status === 'syncing' || state.status === 'unknown') fetchStatus();
        };
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [state.status, fetchStatus]);

    const isSyncing = state.status === 'syncing';
    const isBridgeActive = isSyncing;
    const value = { ...state, refresh: fetchStatus, isSyncing, isBridgeActive };

    return (
        <AmazonSyncContext.Provider value={value}>
            {children}
        </AmazonSyncContext.Provider>
    );
};

export const useAmazonSync = () => useContext(AmazonSyncContext);

export default AmazonSyncContext;
